import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, RequireAuth } from '@/components/AuthProvider';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { validateAuthConfig } from '@/config/authConfig';
import Dashboard from '@/pages/Dashboard';
import Invoices from '@/pages/Invoices';
import NewInvoice from '@/pages/NewInvoice';
import Customers from '@/pages/Customers';
import Settings from '@/pages/Settings';
import VirtualTerminal from '@/pages/VirtualTerminal';
import Transactions from '@/pages/Transactions';
import PaymentComplete from '@/pages/PaymentComplete';
import Products from '@/pages/Products';
import Donations from '@/pages/Donations';
import Layout from '@/components/Layout';
import SignupPage from '@/pages/signup';
import FreeTrialSignup from '@/pages/FreeTrialSignup';
import Onboarding from '@/pages/Onboarding';
import Login from '@/pages/Login';
import TenantDashboard from '@/pages/tenants';
import TenantCreate from '@/pages/tenants/create';
import TenantDetail from '@/pages/tenants/[id]';
import TenantCustomers from '@/pages/tenants/customers';
import TenantLogs from '@/pages/tenants/logs';
import { Toaster } from '@/components/ui/toaster';
import './index.css';
import './lib/i18n'; // Import i18n configuration
import PublicDonation from '@/pages/PublicDonation';
import InvoicePaymentPage from '@/pages/InvoicePaymentPage';
import InvoicePreviewPage from '@/pages/InvoicePreview';
import InvoiceEmailPage from '@/pages/InvoiceEmailPage';
import MerchantApplication from '@/features/merchant-application/MerchantApplication';
import MerchantApplicationsManagement from '@/pages/MerchantApplicationsManagement';
import { MerchantApplicationDetail } from '@/features/merchant-application/components/MerchantApplicationDetail';
import Config from '@/pages/tenants/config';
import { BankingPartnersManagement } from '@/pages/BankingPartnersManagement';
import { ApplicationSourcesManagementPage } from '@/pages/ApplicationSourcesManagement';
import KeycloakOnboarding from '@/pages/KeycloakOnboarding';



// Import InvoiceBug marketing page
import InvoiceBugPage from '@/app/invoicebug/page';

const router = createBrowserRouter([
  {
    path: '/',
    element: <InvoiceBugPage />,
    errorElement: <ErrorBoundary />
  },
  {
    path: '/login',
    element: <Login />,
    errorElement: <ErrorBoundary />
  },
  {
    path: '/signup',
    element: <SignupPage />,
    errorElement: <ErrorBoundary />
  },
  {
    path: '/free-trial-signup',
    element: <FreeTrialSignup />,
    errorElement: <ErrorBoundary />
  },
  {
    path: '/onboarding',
    element: <Onboarding />,
    errorElement: <ErrorBoundary />
  },
  {
    path: '/keycloak-onboarding',
    element: <KeycloakOnboarding />,
    errorElement: <ErrorBoundary />
  },
  {
    path: '/payment-complete',
    element: <PaymentComplete />,
    errorElement: <ErrorBoundary />
  },
  {
    path: '/pay/:invoiceId',
    element: <InvoicePaymentPage />,
    errorElement: <ErrorBoundary />
  },
  {
    // Dynamic donation page route - /:companyName/:slug
    path: '/:companyName/:slug',
    element: <PublicDonation />,
    errorElement: <ErrorBoundary />
  },
  {
    // Legacy donation URL format
    path: '/donate/:slug',
    element: <PublicDonation />,
    errorElement: <ErrorBoundary />
  },
  {
    path: '/app',
    element: (
      <RequireAuth>
        <Layout />
      </RequireAuth>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: <Dashboard />
      },
      {
        path: 'invoices',
        element: <Invoices />
      },
      {
        path: 'invoices/new',
        element: <NewInvoice />
      },
      {
        path: 'invoices/edit/:id',
        element: <NewInvoice />
      },
      {
        path: 'invoices/edit-template/:id',
        element: <NewInvoice />
      },
      {
        path: 'invoices/preview',
        element: <InvoicePreviewPage />
      },
      {
        path: 'invoices/email',
        element: <InvoiceEmailPage />
      },
      {
        path: 'customers',
        element: <Customers />
      },
      {
        path: 'products',
        element: <Products />
      },
      {
        path: 'virtual-terminal',
        element: <VirtualTerminal />
      },
      {
        path: 'transactions',
        element: <Transactions />
      },
      {
        path: 'donations',
        element: <Donations />
      },
      {
        path: 'settings',
        element: <Settings />
      },
      {
        path: 'tenants',
        element: <TenantDashboard />
      },
      {
        path: 'tenants/create',
        element: <TenantCreate />
      },
      {
        path: 'tenants/customers',
        element: <TenantCustomers />
      },
      {
        path: 'tenants/logs',
        element: <TenantLogs />
      },
      {
        path: 'tenants/config',
        element: <Config />
      },
      {
        path: 'tenants/:id',
        element: <TenantDetail />
      },
      {
        path: 'merchant-application',
        element: <MerchantApplication />
      },
      {
        path: 'merchant-applications',
        element: <MerchantApplicationsManagement />
      },
      {
        path: 'merchant-applications/:applicationId',
        element: <MerchantApplicationDetail />
      },
      {
        path: 'banking-partners/*',
        element: <BankingPartnersManagement />
      },
      {
        path: 'application-sources',
        element: <ApplicationSourcesManagementPage />
      }
    ]
  }
]);

// Check if we're in development mode
const isDevelopment = import.meta.env.MODE === 'development';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Disable all caching in development for fresh data, keep production caching
      staleTime: isDevelopment ? 0 : 5 * 60 * 1000,  // Dev: 0, Prod: 5 minutes
      gcTime: isDevelopment ? 0 : 10 * 60 * 1000,     // Dev: 0, Prod: 10 minutes
      retry: isDevelopment ? false : 2,                // Dev: no retry, Prod: 2 retries
      refetchOnWindowFocus: isDevelopment ? true : false, // Dev: always refetch on focus
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <div className="relative">
          <RouterProvider router={router} />
          <Toaster />
        </div>
      </AuthProvider>
    </QueryClientProvider>
  );
}

// Validate authentication configuration before starting the app
try {
  validateAuthConfig();
  console.log('[APP] ✅ Authentication configuration validated successfully');
} catch (error) {
  console.error('[APP] ❌ Authentication configuration validation failed:', error);
  // Continue to render app but user will see configuration errors
  // This allows developers to see what's wrong in the console
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
